import React, { useEffect, useState } from "react";
import api from "@api";
import "./TennisTeamMatchEditor.css";


function TennisTeamMatchEditor({
  game,
  stats = [],
  refreshGame,
  matches: matchesProp,
  setMatches: setMatchesProp,
  refreshRacquetMatches,
}) {
  const [showAddMatchForm, setShowAddMatchForm] = useState(false);
  const [localMatches, setLocalMatches] = useState([]);

  const matches = matchesProp ?? localMatches;
  const setMatches = setMatchesProp ?? setLocalMatches;
  const [lineupEditingMatchId, setLineupEditingMatchId] = useState(null);
  const [openActionMatchId, setOpenActionMatchId] = useState(null);
  const [expandedMatchId, setExpandedMatchId] = useState(null);
  const [scoringMatchId, setScoringMatchId] = useState(null);
  const [statsEditingMatchId, setStatsEditingMatchId] = useState(null);
  const [statInputs, setStatInputs] = useState({});
  const [scoreSetCount, setScoreSetCount] = useState({});
  const [tiebreakSets, setTiebreakSets] = useState({});
  const [scoreInputs, setScoreInputs] = useState({});
  const [draggedMatchId, setDraggedMatchId] = useState(null);
  const [newMatch, setNewMatch] = useState({
    match_type: "singles",
    match_label: "",

    home_player_1_id: "",
    home_player_2_id: "",
    away_player_1_id: "",
    away_player_2_id: "",

    home_roster_1_id: "",
    home_roster_2_id: "",
    away_roster_1_id: "",
    away_roster_2_id: "",
  });

  const [lineupEdit, setLineupEdit] = useState({
    match_type: "singles",
    match_label: "",

    home_player_1_id: "",
    home_player_2_id: "",
    away_player_1_id: "",
    away_player_2_id: "",

    home_roster_1_id: "",
    home_roster_2_id: "",
    away_roster_1_id: "",
    away_roster_2_id: "",
  });

  const fetchRacquetMatches = async () => {
    if (refreshRacquetMatches) {
      await refreshRacquetMatches();
      return;
    }

    if (!game?.id) return;

    try {
      const res = await api.get(`/api/gamesv2/${game.id}/racquet-matches`);
      setMatches(res.data || []);
    } catch (error) {
      console.error("Error fetching racquet matches:", error);
    }
  };

  useEffect(() => {
    fetchRacquetMatches();
  }, [game?.id]);

  const resetNewMatch = () => {
    setNewMatch({
      match_type: "singles",
      match_label: "",

      home_player_1_id: "",
      home_player_2_id: "",
      away_player_1_id: "",
      away_player_2_id: "",

      home_roster_1_id: "",
      home_roster_2_id: "",
      away_roster_1_id: "",
      away_roster_2_id: "",
    });
  };

  const getPlayerName = (teamSide, playerId, rosterId) => {
    const players =
      teamSide === "home"
        ? game?.homeTeam?.players || []
        : game?.awayTeam?.players || [];

    const player = players.find(
      (p) => String(p.id) === String(playerId)
    );

    if (!player) return "Unknown Player";

    return `${player.firstName || ""} ${player.lastName || ""}`.trim();
  };

  const getScoreSummary = (scores = []) => {
    if (!scores.length) return "No score yet";

    return scores
      .map((set) => {
        const homeTb = set.home_tiebreak ? `(${set.home_tiebreak})` : "";
        const awayTb = set.away_tiebreak ? `(${set.away_tiebreak})` : "";

        return `${set.home_score ?? "-"}${homeTb}-${set.away_score ?? "-"}${awayTb}`;
      })
      .join(", ");
  };

  const updateScoreInput = (matchId, setIndex, field, value) => {
    setScoreInputs((prev) => ({
      ...prev,
      [matchId]: {
        ...(prev[matchId] || {}),
        [setIndex]: {
          ...(prev[matchId]?.[setIndex] || {}),
          [field]: value,
        },
      },
    }));
  };

  const openScoreEditor = (match) => {
    const isOpening = scoringMatchId !== match.id;

    setScoringMatchId(isOpening ? match.id : null);
    setExpandedMatchId(isOpening ? match.id : null);
    setLineupEditingMatchId(null);
    setOpenActionMatchId(null);

    if (!isOpening) return;

    const existingScores = match.scores || [];
    const setCount = Math.max(existingScores.length || 1, 1);

    setScoreSetCount((prev) => ({
      ...prev,
      [match.id]: setCount,
    }));

    const nextInputs = {};
    const nextTiebreaks = {};

    existingScores.forEach((score, index) => {
      nextInputs[index] = {
        home_score: score.home_score ?? "",
        away_score: score.away_score ?? "",
        home_tiebreak: score.home_tiebreak ?? "",
        away_tiebreak: score.away_tiebreak ?? "",
      };

      if (score.home_tiebreak || score.away_tiebreak) {
        nextTiebreaks[`${match.id}-${index}`] = true;
      }
    });

    setScoreInputs((prev) => ({
      ...prev,
      [match.id]: nextInputs,
    }));

    setTiebreakSets((prev) => ({
      ...prev,
      ...nextTiebreaks,
    }));
  };

  const handleCreateMatch = async () => {
    if (!newMatch.home_player_1_id || !newMatch.away_player_1_id) {
      alert("Please select one player from each team.");
      return;
    }

    if (
      newMatch.match_type === "doubles" &&
      (!newMatch.home_player_2_id || !newMatch.away_player_2_id)
    ) {
      alert("Please select two players from each team for doubles.");
      return;
    }
    if (
      newMatch.match_type === "doubles" &&
      (
        String(newMatch.home_player_1_id) === String(newMatch.home_player_2_id) ||
        String(newMatch.away_player_1_id) === String(newMatch.away_player_2_id)
      )
    ) {
      alert("A doubles team cannot use the same player twice.");
      return;
    }
    try {
      await api.post(`/api/gamesv2/${game.id}/racquet-matches`, {
        ...newMatch,
        display_order: matches.length + 1,
      });

      resetNewMatch();
      setShowAddMatchForm(false);
      fetchRacquetMatches();
    } catch (error) {
      console.error("Error creating racquet match:", error);
      alert("Failed to create match.");
    }
  };

  const handleUpdateMatch = async (matchId) => {
    if (!lineupEdit.home_player_1_id || !lineupEdit.away_player_1_id) {
      alert("Please select one player from each team.");
      return;
    }

    if (
      lineupEdit.match_type === "doubles" &&
      (!lineupEdit.home_player_2_id || !lineupEdit.away_player_2_id)
    ) {
      alert("Please select two players from each team for doubles.");
      return;
    }
    if (
      lineupEdit.match_type === "doubles" &&
      (
        String(lineupEdit.home_player_1_id) === String(lineupEdit.home_player_2_id) ||
        String(lineupEdit.away_player_1_id) === String(lineupEdit.away_player_2_id)
      )
    ) {
      alert("A doubles team cannot use the same player twice.");
      return;
    }
    try {
      await api.put(`/api/gamesv2/racquet-matches/${matchId}`, lineupEdit);

      setLineupEditingMatchId(null);
      fetchRacquetMatches();
    } catch (error) {
      console.error("Error updating racquet match:", error);
      alert("Failed to update match.");
    }
  };

  const handleDeleteMatch = async (matchId) => {
    try {
      await api.delete(`/api/gamesv2/racquet-matches/${matchId}`);

      setOpenActionMatchId(null);
      setExpandedMatchId(null);
      setScoringMatchId(null);

      fetchRacquetMatches();

      if (refreshGame) {
        refreshGame();
      }
    } catch (error) {
      console.error("Error deleting racquet match:", error);
      alert("Failed to delete match.");
    }
  };

  const handleSaveScores = async (match) => {
    try {
      const savedScores = Array.from({
        length: scoreSetCount[match.id] || 1,
      }).map((_, index) => ({
        home_score: scoreInputs[match.id]?.[index]?.home_score || "",
        away_score: scoreInputs[match.id]?.[index]?.away_score || "",
        home_tiebreak: scoreInputs[match.id]?.[index]?.home_tiebreak || "",
        away_tiebreak: scoreInputs[match.id]?.[index]?.away_tiebreak || "",
      }));

      await api.put(`/api/gamesv2/racquet-matches/${match.id}/scores`, {
        scores: savedScores,
      });

      setScoringMatchId(null);
      setExpandedMatchId(null);

      fetchRacquetMatches();

      if (refreshGame) {
        refreshGame();
      }
    } catch (error) {
      console.error("Error saving racquet scores:", error);
      alert("Failed to save score.");
    }
  };

  const handleReorderMatches = async (targetMatchId) => {
    if (!draggedMatchId || draggedMatchId === targetMatchId) return;

    const draggedIndex = matches.findIndex((match) => match.id === draggedMatchId);
    const targetIndex = matches.findIndex((match) => match.id === targetMatchId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const reordered = [...matches];
    const [draggedItem] = reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, draggedItem);

    const updatedMatches = reordered.map((match, index) => ({
      ...match,
      display_order: index + 1,
    }));

    setMatches(updatedMatches);
    setDraggedMatchId(null);

    try {
      await api.put(`/api/gamesv2/${game.id}/racquet-matches/reorder`, {
        matches: updatedMatches.map((match) => ({
          id: match.id,
          display_order: match.display_order,
        })),
      });

      fetchRacquetMatches();
    } catch (error) {
      console.error("Error reordering matches:", error);
      alert("Failed to reorder matches.");
      fetchRacquetMatches();
    }
  };

  const getMatchPlayers = (match) => {
    const players = [];

    if (match.home_player_1_id) {
      players.push({
        side: "home",
        player_id: match.home_player_1_id,
        roster_id: match.home_roster_1_id,
        name: getPlayerName("home", match.home_player_1_id, match.home_roster_1_id),
      });
    }

    if (match.home_player_2_id) {
      players.push({
        side: "home",
        player_id: match.home_player_2_id,
        roster_id: match.home_roster_2_id,
        name: getPlayerName("home", match.home_player_2_id, match.home_roster_2_id),
      });
    }

    if (match.away_player_1_id) {
      players.push({
        side: "away",
        player_id: match.away_player_1_id,
        roster_id: match.away_roster_1_id,
        name: getPlayerName("away", match.away_player_1_id, match.away_roster_1_id),
      });
    }

    if (match.away_player_2_id) {
      players.push({
        side: "away",
        player_id: match.away_player_2_id,
        roster_id: match.away_roster_2_id,
        name: getPlayerName("away", match.away_player_2_id, match.away_roster_2_id),
      });
    }

    return players;
  };

  const getPlayerStatKey = (player) =>
    `player-${player.player_id}`;

  const openStatsEditor = (match) => {
    const isOpening = statsEditingMatchId !== match.id;

    if (!isOpening) {
      setStatsEditingMatchId(null);
      return;
    }

    const existingInputs = {};

    (match.stats || []).forEach((entry) => {
      if (!entry?.player_id || !entry?.stat_id) return;

      const playerKey = `player-${entry.player_id}`;

      if (!existingInputs[playerKey]) {
        existingInputs[playerKey] = {};
      }

      existingInputs[playerKey][entry.stat_id] =
        entry.value ?? "";
    });

    setStatInputs((prev) => ({
      ...prev,
      [match.id]: existingInputs,
    }));

    setStatsEditingMatchId(match.id);
    setLineupEditingMatchId(null);
    setScoringMatchId(null);
    setOpenActionMatchId(null);
    setExpandedMatchId(match.id);
  };
  const handleSaveMatchStats = async (match) => {
    try {
      const matchInputs = statInputs[match.id] || {};
      const matchPlayers = getMatchPlayers(match);

      const statsToSave = [];

      matchPlayers.forEach((player) => {
        const playerKey = getPlayerStatKey(player);
        const playerInputs = matchInputs[playerKey] || {};
        stats.forEach((stat) => {
          const isWinnersStat =
            stat.abbreviation === "W" || stat.shortName === "W";

          statsToSave.push({
            player_id: player.player_id,
            roster_id: player.roster_id || null,
            stat_id: stat.id,
            value: isWinnersStat
              ? getCalculatedWinners(match.id, getPlayerStatKey(player))
              : playerInputs[stat.id] || 0,
          });
        });
      });

      await api.put(`/api/gamesv2/racquet-matches/${match.id}/stats`, {
        stats: statsToSave,
      });

      setStatsEditingMatchId(null);
      fetchRacquetMatches();
    } catch (error) {
      console.error("Error saving match stats:", error);
      alert("Failed to save match stats.");
    }
  };

  const getStatByAbbr = (abbr) =>
    stats.find((stat) => stat.abbreviation === abbr || stat.shortName === abbr);

  const getInputValue = (matchId, playerKey, statId) =>
    statInputs[matchId]?.[playerKey]?.[statId] || "";

  const getCalculatedWinners = (matchId, playerKey) => {
    const fwStat = getStatByAbbr("FW");
    const bwStat = getStatByAbbr("BW");

    const fw = Number(getInputValue(matchId, playerKey, fwStat?.id) || 0);
    const bw = Number(getInputValue(matchId, playerKey, bwStat?.id) || 0);

    return fw + bw;
  };


  const TENNIS_STAT_ORDER = [
    "ACE",
    "1SA",
    "1SI",
    "2SA",
    "2SI",
    "SM",
    "SX",
    "FW",
    "BW",
    "W",
  ];

  const orderedStats = [...stats].sort((a, b) => {
    const aIndex = TENNIS_STAT_ORDER.indexOf(a.abbreviation);
    const bIndex = TENNIS_STAT_ORDER.indexOf(b.abbreviation);

    return (aIndex === -1 ? 999 : aIndex) -
      (bIndex === -1 ? 999 : bIndex);
  });





  return (
    <div className="tennis-match-editor">
      <div className="tennis-match-editor-header">
        <div>
          <h3>Match Lineup</h3>
          <p>Manage singles and doubles matches for this team game.</p>
        </div>

        {!showAddMatchForm && (
          <button
            type="button"
            className="submit-button"
            onClick={() => {
              setShowAddMatchForm(true);
              resetNewMatch();
            }}
          >
            + Add Match
          </button>
        )}
      </div>

      {showAddMatchForm && (
        <div className="tennis-match-form">
          <h4>Add Match</h4>

          <label>
            Match Type
            <select
              value={newMatch.match_type}
              onChange={(e) =>
                setNewMatch({
                  ...newMatch,
                  match_type: e.target.value,

                  home_player_1_id: "",
                  home_player_2_id: "",
                  away_player_1_id: "",
                  away_player_2_id: "",

                  home_roster_1_id: "",
                  home_roster_2_id: "",
                  away_roster_1_id: "",
                  away_roster_2_id: "",
                })
              }
            >
              <option value="singles">Singles</option>
              <option value="doubles">Doubles</option>
            </select>
          </label>
          <label>
            Match Label
            <input
              type="text"
              placeholder="Singles 1, Doubles 1, Court 1..."
              value={newMatch.match_label}
              onChange={(e) =>
                setNewMatch({
                  ...newMatch,
                  match_label: e.target.value,
                })
              }
            />
          </label>
          <div className="tennis-player-select-grid">
            <label>
              {game?.homeTeam?.name || "Home Team"} Player 1
              <select
                value={newMatch.home_player_1_id}
                onChange={(e) => {
                  const selectedPlayer = game?.homeTeam?.players?.find(
                    (player) => String(player.id) === String(e.target.value)
                  );

                  setNewMatch({
                    ...newMatch,
                    home_player_1_id: e.target.value,
                    home_roster_1_id: selectedPlayer?.roster_id || "",
                  });
                }}
              >
                <option value="">Select player</option>
                {game?.homeTeam?.players?.map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.firstName} {player.lastName}
                  </option>
                ))}
              </select>
            </label>

            <label>
              {game?.awayTeam?.name || "Away Team"} Player 1
              <select
                value={newMatch.away_player_1_id}
                onChange={(e) => {
                  const selectedPlayer = game?.awayTeam?.players?.find(
                    (player) => String(player.id) === String(e.target.value)
                  );

                  setNewMatch({
                    ...newMatch,
                    away_player_1_id: e.target.value,
                    away_roster_1_id: selectedPlayer?.roster_id || "",
                  });
                }}
              >
                <option value="">Select player</option>
                {game?.awayTeam?.players?.map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.firstName} {player.lastName}
                  </option>
                ))}
              </select>
            </label>

            {newMatch.match_type === "doubles" && (
              <>
                <label>
                  {game?.homeTeam?.name || "Home Team"} Player 2
                  <select
                    value={newMatch.home_player_2_id}
                    onChange={(e) => {
                      const selectedPlayer = game?.homeTeam?.players?.find(
                        (player) => String(player.id) === String(e.target.value)
                      );

                      setNewMatch({
                        ...newMatch,
                        home_player_2_id: e.target.value,
                        home_roster_2_id: selectedPlayer?.roster_id || "",
                      });
                    }}
                  >
                    <option value="">Select player</option>
                    {game?.homeTeam?.players?.map((player) => (
                      <option key={player.id} value={player.id}>
                        {player.firstName} {player.lastName}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  {game?.awayTeam?.name || "Away Team"} Player 2
                  <select
                    value={newMatch.away_player_2_id}
                    onChange={(e) => {
                      const selectedPlayer = game?.awayTeam?.players?.find(
                        (player) => String(player.id) === String(e.target.value)
                      );

                      setNewMatch({
                        ...newMatch,
                        away_player_2_id: e.target.value,
                        away_roster_2_id: selectedPlayer?.roster_id || "",
                      });
                    }}
                  >
                    <option value="">Select player</option>
                    {game?.awayTeam?.players?.map((player) => (
                      <option key={player.id} value={player.id}>
                        {player.firstName} {player.lastName}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}
          </div>

          <div className="tennis-score-actions">
            <button
              type="button"
              className="tennis-match-small-button tennis-action-cancel"
              onClick={() => {
                setShowAddMatchForm(false);
                resetNewMatch();
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              className="tennis-match-small-button tennis-action-save"
              onClick={handleCreateMatch}
            >
              Save Match
            </button>
          </div>
        </div>
      )}

      {matches.length > 0 && (
        <div className="tennis-match-preview-list">

          {matches.map((match) => {
            const isExpanded =
              expandedMatchId === match.id ||
              scoringMatchId === match.id ||
              lineupEditingMatchId === match.id ||
              !match.scores?.length;

            return (
              <div
                key={match.id}
                className="tennis-match-preview-card"
                draggable
                onDragStart={() => setDraggedMatchId(match.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleReorderMatches(match.id)}
              >
                <div className="tennis-match-card-top">
                  <div className="tennis-match-card-title-row">
                    <span style={{ cursor: "grab" }}>☰</span>
                    <span>{match.match_label || `Match ${match.display_order}`}</span>
                    <span className="tennis-match-type-pill">
                      {match.match_type === "singles" ? "Singles" : "Doubles"}
                    </span>
                  </div>

                  <div className="tennis-match-card-actions">
                    {scoringMatchId !== match.id && (
                      <>
                      <button
                        type="button"
                        className="tennis-match-small-button"
                        onClick={() => openScoreEditor(match)}
                      >
                        {match.scores?.length > 0 ? "Edit Score" : "Enter Score"}
                      </button>
       

                    <div className="tennis-more-actions-wrap">
                      <button
                        type="button"
                        className="tennis-match-small-button"
                        onClick={() => {
                          setOpenActionMatchId(
                            openActionMatchId === match.id ? null : match.id
                          );
                        }}
                      >
                        More
                      </button>

                      {openActionMatchId === match.id && (
                        <div className="tennis-more-actions-menu">
                  
                              <button
                                type="button"
                                className="tennis-more-action-item"
                                onClick={() => openStatsEditor(match)}
                              >
                                {statsEditingMatchId === match.id
                                  ? "Cancel Stats"
                                  : "Edit Stats"}
                              </button>
                          <button
                            type="button"
                            className="tennis-more-action-item tennis-more-action-danger"
                            onClick={() => handleDeleteMatch(match.id)}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                      </>
                    )}
                  </div>
                  
                </div>

                {!isExpanded && (
                  <div className="tennis-collapsed-match">
                    <div>
                      <div className="tennis-collapsed-players">
                        {match.match_type === "singles" ? (
                          <div className="tennis-singles-matchup-line">
                            <span>
                              {getPlayerName("home", match.home_player_1_id, match.home_roster_1_id)}
                            </span>

                            <span className="tennis-inline-vs">vs</span>

                            <span>
                              {getPlayerName("away", match.away_player_1_id, match.away_roster_1_id)}
                            </span>
                          </div>
                        ) : (
                          <div className="tennis-doubles-matchup-block">
                            <div className="tennis-doubles-pair">
                              {getPlayerName("home", match.home_player_1_id, match.home_roster_1_id)} /{" "}
                              {getPlayerName("home", match.home_player_2_id, match.home_roster_2_id)}
                            </div>

                            <div className="tennis-doubles-vs">vs</div>

                            <div className="tennis-doubles-pair">
                              {getPlayerName("away", match.away_player_1_id, match.away_roster_1_id)} /{" "}
                              {getPlayerName("away", match.away_player_2_id, match.away_roster_2_id)}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="tennis-collapsed-score">
                        {getScoreSummary(match.scores)}
                      </div>
                    </div>

                    <button
                      type="button"
                      className="tennis-match-small-button"
                      onClick={() =>
                        setExpandedMatchId(expandedMatchId === match.id ? null : match.id)
                      }
                    >
                      {expandedMatchId === match.id ? "Hide Details" : "Details"}
                    </button>
                  </div>
                )}

                {isExpanded && (
                  <>
                    {lineupEditingMatchId === match.id && (
                      <div className="tennis-inline-lineup-form">
                        <label>
                          Match Type
                          <select
                            value={lineupEdit.match_type}
                            onChange={(e) =>
                              setLineupEdit({
                                ...lineupEdit,
                                match_type: e.target.value,

                                home_player_2_id: "",
                                away_player_2_id: "",

                                home_roster_2_id: "",
                                away_roster_2_id: "",
                              })
                            }
                          >
                            <option value="singles">Singles</option>
                            <option value="doubles">Doubles</option>
                          </select>
                        </label>
                        <label>
                          Match Label
                          <input
                            type="text"
                            placeholder="Singles 1, Doubles 1, Court 1..."
                            value={lineupEdit.match_label}
                            onChange={(e) =>
                              setLineupEdit({
                                ...lineupEdit,
                                match_label: e.target.value,
                              })
                            }
                          />
                        </label>
                        <div className="tennis-player-select-grid">
                          <label>
                            {game?.homeTeam?.name || "Home Team"} Player 1
                            <select
                              value={lineupEdit.home_player_1_id}
                              onChange={(e) => {
                                const selectedPlayer = game?.homeTeam?.players?.find(
                                  (player) => String(player.id) === String(e.target.value)
                                );

                                setLineupEdit({
                                  ...lineupEdit,
                                  home_player_1_id: e.target.value,
                                  home_roster_1_id: selectedPlayer?.roster_id || "",
                                });
                              }}
                            >
                              <option value="">Select player</option>
                              {game?.homeTeam?.players?.map((player) => (
                                <option key={player.id} value={player.id}>
                                  {player.firstName} {player.lastName}
                                </option>
                              ))}
                            </select>
                          </label>

                          <label>
                            {game?.awayTeam?.name || "Away Team"} Player 1
                            <select
                              value={lineupEdit.away_player_1_id}
                              onChange={(e) => {
                                const selectedPlayer = game?.awayTeam?.players?.find(
                                  (player) => String(player.id) === String(e.target.value)
                                );

                                setLineupEdit({
                                  ...lineupEdit,
                                  away_player_1_id: e.target.value,
                                  away_roster_1_id: selectedPlayer?.roster_id || "",
                                });
                              }}
                            >
                              <option value="">Select player</option>
                              {game?.awayTeam?.players?.map((player) => (
                                <option key={player.id} value={player.id}>
                                  {player.firstName} {player.lastName}
                                </option>
                              ))}
                            </select>
                          </label>

                          {lineupEdit.match_type === "doubles" && (
                            <>
                              <label>
                                {game?.homeTeam?.name || "Home Team"} Player 2
                                <select
                                  value={lineupEdit.home_player_2_id}
                                  onChange={(e) => {
                                    const selectedPlayer = game?.homeTeam?.players?.find(
                                      (player) => String(player.id) === String(e.target.value)
                                    );

                                    setLineupEdit({
                                      ...lineupEdit,
                                      home_player_2_id: e.target.value,
                                      home_roster_2_id: selectedPlayer?.roster_id || "",
                                    });
                                  }}
                                >
                                  <option value="">Select player</option>
                                  {game?.homeTeam?.players?.map((player) => (
                                    <option key={player.id} value={player.id}>
                                      {player.firstName} {player.lastName}
                                    </option>
                                  ))}
                                </select>
                              </label>

                              <label>
                                {game?.awayTeam?.name || "Away Team"} Player 2
                                <select
                                  value={lineupEdit.away_player_2_id}
                                  onChange={(e) => {
                                    const selectedPlayer = game?.awayTeam?.players?.find(
                                      (player) => String(player.id) === String(e.target.value)
                                    );

                                    setLineupEdit({
                                      ...lineupEdit,
                                      away_player_2_id: e.target.value,
                                      away_roster_2_id: selectedPlayer?.roster_id || "",
                                    });
                                  }}
                                >
                                  <option value="">Select player</option>
                                  {game?.awayTeam?.players?.map((player) => (
                                    <option key={player.id} value={player.id}>
                                      {player.firstName} {player.lastName}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            </>
                          )}
                        </div>

                        <div className="tennis-score-actions">
                          <button
                            type="button"
                            className="tennis-match-small-button"
                            onClick={() => handleUpdateMatch(match.id)}
                          >
                            Save Match
                          </button>

                          <button
                            type="button"
                            className="tennis-match-small-button"
                            onClick={() => setLineupEditingMatchId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {statsEditingMatchId === match.id && (
                      <div className="tennis-match-stats-panel">
                        <div className="tennis-match-stats-header">
                          <h4>Match Stats</h4>
                          <span>Stats are saved for this match only</span>
                        </div>

                        <div className="tennis-match-stats-table">
                          <div className="tennis-match-stats-row tennis-match-stats-head">
                            <div className="tennis-match-stats-player-col">Player</div>

                            {orderedStats.map((stat) => (
                              <div key={stat.id} className="tennis-match-stats-stat-col">
                                {stat.name || stat.abbreviation || stat.shortName}
                              </div>
                            ))}
                          </div>

                          {getMatchPlayers(match).map((player) => (
                            <div
                              key={`${player.side}-${player.player_id}`}
                              className="tennis-match-stats-row"
                            >
                              <div className="tennis-match-stats-player-col">
                                <span className="tennis-match-stats-player-name">
                                  {player.name}
                                </span>
                                <span className="tennis-match-stats-player-side">
                                  {player.side === "home"
                                    ? game?.homeTeam?.name || "Home"
                                    : game?.awayTeam?.name || "Away"}
                                </span>
                              </div>

                              {orderedStats.map((stat) => {
                                const isWinnersStat =
                                  stat.abbreviation === "W" || stat.shortName === "W";

                                return (
                                  <div key={stat.id} className="tennis-match-stats-stat-col">
                                    {isWinnersStat ? (
                                      <div className="tennis-calculated-stat">
                                        {getCalculatedWinners(match.id, getPlayerStatKey(player))}
                                      </div>
                                    ) : (
                                      <input
                                        type="number"
                                        min="0"
                                          value={
                                            statInputs[match.id]?.[getPlayerStatKey(player)]?.[stat.id] || ""
                                          }
                                        onChange={(e) =>
                                          setStatInputs((prev) => ({
                                            ...prev,
                                            [match.id]: {
                                              ...(prev[match.id] || {}),
                                              [getPlayerStatKey(player)]: {
                                                ...(prev[match.id]?.[getPlayerStatKey(player)] || {}),
                                                [stat.id]: e.target.value,
                                              },
                                            },
                                          }))
                                        }
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>

                        <div className="tennis-score-actions">
                          <button
                            type="button"
                            className="tennis-match-small-button"
                            onClick={() => handleSaveMatchStats(match)}
                          >
                            Save Stats
                          </button>

                          <button
                            type="button"
                            className="tennis-match-small-button"
                            onClick={() => setStatsEditingMatchId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}


                    <div
                      className="tennis-scoreboard-grid"
                      style={{
                        gridTemplateColumns: `1fr repeat(${scoringMatchId === match.id
                            ? scoreSetCount[match.id] || 1
                            : Math.max(match.scores?.length || 1, 1)
                          }, 64px)`,
                      }}
                    >
                      <div></div>

                      {Array.from({
                        length:
                          scoringMatchId === match.id
                            ? scoreSetCount[match.id] || 1
                            : Math.max(match.scores?.length || 1, 1),
                      }).map((_, index) => {
                        const tbKey = `${match.id}-${index}`;

                        return (
                          <div key={index} className="tennis-scoreboard-set-header">
                            <span>Set {index + 1}</span>

                            {scoringMatchId === match.id && (
                              <button
                                type="button"
                                className={`tennis-tb-button ${tiebreakSets[tbKey]
                                    ? "tennis-tb-button-active"
                                    : ""
                                  }`}
                                onClick={() => {
                                  const isRemovingTb = tiebreakSets[tbKey];

                                  setTiebreakSets((prev) => ({
                                    ...prev,
                                    [tbKey]: !prev[tbKey],
                                  }));

                                  if (isRemovingTb) {
                                    setScoreInputs((prev) => ({
                                      ...prev,
                                      [match.id]: {
                                        ...(prev[match.id] || {}),
                                        [index]: {
                                          ...(prev[match.id]?.[index] || {}),
                                          home_tiebreak: "",
                                          away_tiebreak: "",
                                        },
                                      },
                                    }));
                                  }
                                }}
                              >
                                {tiebreakSets[tbKey] ? "- TB" : "+ TB"}
                              </button>
                            )}
                          </div>
                        );
                      })}

                      <div className="tennis-scoreboard-player">
                        <span className="tennis-scoreboard-team">
                          {game?.homeTeam?.name || "Home Team"}
                        </span>
                        <span className="tennis-scoreboard-name">
                          {match.match_type === "singles" && (
                            <>{getPlayerName("home", match.home_player_1_id, match.home_roster_1_id)}</>
                          )}

                          {match.match_type === "doubles" && (
                            <>
                              {getPlayerName("home", match.home_player_1_id, match.home_roster_1_id)} /{" "}
                              {getPlayerName("home", match.home_player_2_id, match.home_roster_2_id)}
                            </>
                          )}
                        </span>
                      </div>

                      {Array.from({
                        length:
                          scoringMatchId === match.id
                            ? scoreSetCount[match.id] || 1
                            : Math.max(match.scores?.length || 1, 1),
                      }).map((_, index) => {
                        const tbKey = `${match.id}-${index}`;

                        if (scoringMatchId === match.id) {
                          return (
                            <div key={index} className="tennis-score-input-group">
                              <input
                                type="number"
                                min="0"
                                value={
                                  scoreInputs[match.id]?.[index]?.home_score || ""
                                }
                                onChange={(e) =>
                                  updateScoreInput(
                                    match.id,
                                    index,
                                    "home_score",
                                    e.target.value
                                  )
                                }
                              />

                              {tiebreakSets[tbKey] && (
                                <input
                                  type="number"
                                  min="0"
                                  placeholder="TB"
                                  value={
                                    scoreInputs[match.id]?.[index]
                                      ?.home_tiebreak || ""
                                  }
                                  onChange={(e) =>
                                    updateScoreInput(
                                      match.id,
                                      index,
                                      "home_tiebreak",
                                      e.target.value
                                    )
                                  }
                                />
                              )}
                            </div>
                          );
                        }

                        return (
                          <div key={index} className="tennis-scoreboard-score-cell">
                            {match.scores?.[index]?.home_score ?? "-"}
                            {match.scores?.[index]?.home_tiebreak && (
                              <sup>{match.scores[index].home_tiebreak}</sup>
                            )}
                          </div>
                        );
                      })}

                      <div className="tennis-scoreboard-player">
                        <span className="tennis-scoreboard-team">
                          {game?.awayTeam?.name || "Away Team"}
                        </span>
                        <span className="tennis-scoreboard-name">
                          {match.match_type === "singles" && (
                            <>{getPlayerName("away", match.away_player_1_id, match.away_roster_1_id)}</>
                          )}

                          {match.match_type === "doubles" && (
                            <>
                              {getPlayerName("away", match.away_player_1_id, match.away_roster_1_id)} /{" "}
                              {getPlayerName("away", match.away_player_2_id, match.away_roster_2_id)}
                            </>
                          )}
                        </span>
                      </div>

                      {Array.from({
                        length:
                          scoringMatchId === match.id
                            ? scoreSetCount[match.id] || 1
                            : Math.max(match.scores?.length || 1, 1),
                      }).map((_, index) => {
                        const tbKey = `${match.id}-${index}`;

                        if (scoringMatchId === match.id) {
                          return (
                            <div key={index} className="tennis-score-input-group">
                              <input
                                type="number"
                                min="0"
                                value={
                                  scoreInputs[match.id]?.[index]?.away_score || ""
                                }
                                onChange={(e) =>
                                  updateScoreInput(
                                    match.id,
                                    index,
                                    "away_score",
                                    e.target.value
                                  )
                                }
                              />

                              {tiebreakSets[tbKey] && (
                                <input
                                  type="number"
                                  min="0"
                                  placeholder="TB"
                                  value={
                                    scoreInputs[match.id]?.[index]
                                      ?.away_tiebreak || ""
                                  }
                                  onChange={(e) =>
                                    updateScoreInput(
                                      match.id,
                                      index,
                                      "away_tiebreak",
                                      e.target.value
                                    )
                                  }
                                />
                              )}
                            </div>
                          );
                        }

                        return (
                          <div key={index} className="tennis-scoreboard-score-cell">
                            {match.scores?.[index]?.away_score ?? "-"}
                            {match.scores?.[index]?.away_tiebreak && (
                              <sup>{match.scores[index].away_tiebreak}</sup>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {!scoringMatchId && !lineupEditingMatchId && !statsEditingMatchId && (
                      <div className="tennis-expanded-close-row tennis-expanded-close-row-bottom">
                        <button
                          type="button"
                          className="tennis-match-small-button"
                          onClick={() => setExpandedMatchId(null)}
                        >
                          Hide Details
                        </button>
                      </div>
                    )}

                    {scoringMatchId === match.id && (
                      <div className="tennis-score-actions">
                        <button
                          type="button"
                          className="tennis-match-small-button tennis-action-cancel"
                          onClick={() => {
                            setScoringMatchId(null);
                            setExpandedMatchId(null);
                          }}
                        >
                          Cancel
                        </button>

                        <button
                          type="button"
                          className="tennis-match-small-button tennis-action-save"
                          onClick={() => handleSaveScores(match)}
                        >
                          Save Score
                        </button>

                        <button
                          type="button"
                          className="tennis-match-small-button"
                          onClick={() => {
                            setScoreSetCount((prev) => ({
                              ...prev,
                              [match.id]: Math.min((prev[match.id] || 1) + 1, 5),
                            }));
                          }}
                        >
                          + Add Set
                        </button>

                        <button
                          type="button"
                          className="tennis-match-small-button tennis-match-remove-button"
                          disabled={(scoreSetCount[match.id] || 1) <= 1}
                          onClick={() => {
                            setScoreSetCount((prev) => ({
                              ...prev,
                              [match.id]: Math.max((prev[match.id] || 1) - 1, 1),
                            }));
                          }}
                        >
                          Remove Set
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TennisTeamMatchEditor;